import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { SearchableDropdown } from "components/designV2/searchableDropdown/searchableDropdown";
import { SegmentController } from "components/designV2/segmentcontroller/segmentcontroller";
import { Text } from "components/Typography/text.styled";
import { FontSizeV2 } from "config/font";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  ControlsRow,
  HeaderRow,
  IntervalSlot,
  OptionsSlot,
  Toolbar,
} from "./scheduleToolbar.styled";
import { UnitTeamDropdown } from "./unitTeamDropdown.component";

import {
  GroupByOption,
  IntervalOption,
  SizeOption,
} from "../hooks/useScheduleToolbarFilters";
import type { UnitTeamOption } from "./unitTeamDropdown.component";

interface Props {
  groupByOption: GroupByOption;
  intervalOption: IntervalOption;
  sizeOption: SizeOption;
  unitTeamOptions: UnitTeamOption[];
  selectedWorkGroupIds: string[];
  onSelectedWorkGroupIdsChange: (values: string[]) => void;
  personOptions: Array<{ value: string; label: string }>;
  selectedPersonIds: string[];
  onSelectedPersonIdsChange: (values: string[]) => void;
  onScheduleClick: () => void;
  onGroupByChange: (value: GroupByOption) => void;
  onIntervalChange: (value: IntervalOption) => void;
  onSizeChange: (value: SizeOption) => void;
  onOptionsClick: () => void;
}

const INTERVAL_OPTIONS: Array<{ value: IntervalOption; labelKey: string }> = [
  { value: "weekly", labelKey: "Schedule.Weekly" },
  { value: "monthly", labelKey: "Schedule.Monthly" },
];

const buildSegmentOptions = <T extends string>(
  options: Array<{ value: T; labelKey: string }>,
  translate: (key: string) => string,
): Array<{ value: T; label: string }> =>
  options.map((option) => ({
    value: option.value,
    label: translate(option.labelKey),
  }));

export const ScheduleToolbar: React.FC<Props> = (props) => {
  const { t } = useTranslation();

  const intervalOptions = useMemo(
    () => buildSegmentOptions(INTERVAL_OPTIONS, t),
    [t],
  );

  const handleIntervalChange = (value: string): void => {
    if (value === "weekly" || value === "monthly") {
      props.onIntervalChange(value);
    }
  };

  return (
    <Toolbar>
      <HeaderRow>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          {/** TODO: Delete beta mark when tested and ready */}
          <Text fontSize={FontSizeV2.H2}>{t("Schedule.Scheduling")}</Text>
          <Text
            fontSize={FontSizeV2.Small}
            style={{
              marginLeft: 10,
              marginTop: 20,
              background: "rgba(251, 193, 0, 0.3)",
              color: "#CF9F00",
              padding: 8,
              borderRadius: 10,
            }}
          >
            {"(Beta)"}
          </Text>
        </div>
        <PrimaryButton
          label={t("Schedule.Schedule")}
          iconName="v2/plus"
          onClick={props.onScheduleClick}
        />
      </HeaderRow>
      <ControlsRow>
        <IntervalSlot>
          <SegmentController
            options={intervalOptions}
            value={props.intervalOption}
            onChange={handleIntervalChange}
          />
        </IntervalSlot>
        <UnitTeamDropdown
          label={t("Schedule.UnitTeam")}
          placeholder={t("Schedule.AllUnitsTeamsVisible")}
          options={props.unitTeamOptions}
          selectedTeamIds={props.selectedWorkGroupIds}
          onSelectedTeamIdsChange={props.onSelectedWorkGroupIdsChange}
        />
        <SearchableDropdown
          label={t("Schedule.Person")}
          placeholder={t("Schedule.SearchOrBrowse")}
          emptyText={t("Schedule.NoPersonMatches")}
          options={props.personOptions}
          leadingIconName="v2/search"
          multiSelect
          selectedValues={props.selectedPersonIds}
          onSelectedValuesChange={props.onSelectedPersonIdsChange}
          onClear={() => props.onSelectedPersonIdsChange([])}
        />
        <OptionsSlot>
          <SecondaryButton
            label={t("Schedule.Options")}
            size="small"
            iconName="v2/settings"
            onClick={props.onOptionsClick}
          />
        </OptionsSlot>
      </ControlsRow>
    </Toolbar>
  );
};
